import { useMemo, useCallback } from 'react';
import { Divider } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useParams } from 'react-router-dom';
import { enqueueSnackbar } from 'notistack';
import { AxiosError } from 'axios';
import { useTranslation } from 'react-i18next';
import { useQuery, useMutation } from 'react-query';
import { GenerativeModelFile } from 'entities/generative-models/types';
import {
	getGenerativeModelFilesList,
	postGenerativeModelFile,
	deleteGenerativeModelFile,
} from 'entities/generative-models/api';
import { showErrorNotification } from 'shared/helpers/showErrorNotification';
import { DnDImportFiles } from 'shared/ui/DndImportFiles';
import { PageHeader } from 'shared/ui/PageHeader';
import { Loader } from 'shared/ui/Loader';
import { EmptyTable } from 'shared/ui/EmptyTable';
import { TableWithHeader } from 'shared/ui/tables/TableWithHeader';
import { ColorPalette } from 'shared/consts/colorPalette';
import {
	GenerativeModelFilesColumns,
	GenerativeModelFilesTableColumns,
} from '../partials/columns/GenerativeModelFilesTableColumns';

const GenerativeUpload = () => {
	const navigate = useNavigate();
	const { t } = useTranslation();
	const { generativeModelId } = useParams();

	const {
		refetch,
		data: filesList,
		isLoading: getIsLoading,
	} = useQuery({
		queryKey: ['get/generative-models-files-list', generativeModelId],
		queryFn: () => getGenerativeModelFilesList({ generativeModelId: generativeModelId || '' }),
		onSuccess: (response) => {
			const isUploading = response.data.filter(
				(sessionItem) => sessionItem.status === 'UPLOADING',
			).length;
			if (isUploading) {
				setTimeout(() => {
					refetch();
				}, 6000);
			}
		},
		onError: (err) => {
			showErrorNotification(err as AxiosError);
		},
	});

	const { mutate: uploadFile, isLoading: postUploadIsLoading } = useMutation({
		mutationKey: ['post/generative-model-file'],
		mutationFn: postGenerativeModelFile,
		onSuccess: () => refetch(),
		onError: (err) => {
			showErrorNotification(err as AxiosError);
		},
	});

	const { mutate: deleteFile, isLoading: deleteIsLoading } = useMutation({
		mutationKey: ['delete/generative-model-file'],
		mutationFn: deleteGenerativeModelFile,
		onSuccess: () => refetch(),
		onError: (err) => {
			showErrorNotification(err as AxiosError);
		},
	});

	const uploadedFilesHandler = (files: Array<File>) => {
		if (files && files?.length > 1) {
			enqueueSnackbar({ message: t('shared_texts.import_csv_file_only_one'), variant: 'error' });
		} else if (files && generativeModelId) {
			uploadFile({ generativeModelId: generativeModelId, data: { file: files[0] } });
		}
	};

	const openEditor = useCallback((file: GenerativeModelFile) => {
		navigate(`/generative-model/${generativeModelId}/file-editor/${file._id}`);
	}, []);

	const deleteHandler = useCallback((file: GenerativeModelFile) => {
		deleteFile({ ids: [file._id] });
	}, []);

	const preparedModels: GenerativeModelFilesColumns[] = useMemo(() => {
		if (!filesList?.data.length) return [];
		return filesList?.data.map((item) => ({
			id: item._id,
			openEditor,
			deleteHandler,
			...item,
		}));
	}, [filesList]);

	if (deleteIsLoading || getIsLoading) return <Loader />;

	return (
		<>
			<PageHeader
				title={t('start_recognition.upload_files_section.title')}
				description={t('start_recognition.upload_from_pc')}
			/>

			<DnDImportFiles
				setUploadedFiles={uploadedFilesHandler}
				fileDescription={t('start_recognition.how_to_start.step_2.li_1')}
				importingProccess={{
					loading: postUploadIsLoading,
					importedFiles: 0,
				}}
			/>

			<Divider color={ColorPalette.periwinkle} sx={{ margin: '20px 0' }} />

			{filesList?.data.length ? (
				<TableWithHeader columns={GenerativeModelFilesTableColumns} rows={preparedModels} />
			) : (
				<EmptyTable text={t('shared_texts.no_uploaded_files')} />
			)}
		</>
	);
};

export default GenerativeUpload;
